import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  FaUsers, FaBookMedical, FaCheckCircle, FaChartBar, FaHospital,
  FaClipboardList, FaFileAlt, FaThumbsUp, FaClock, FaLayerGroup, FaHistory,
  FaDownload
} from 'react-icons/fa';
import {
  MAROON, CustomTooltip, Card, StatCard, Badge, SectionHeader, Spinner, Modal,
  VerifDots, fmtShort, rateColor
} from './monitorUI';
import collegeConfig from '../../config/collegeConfig';
import { generateStudentReportPDF } from '../../utils/monitorReportGenerator';

// Same detail modals used by MonitorDashboard, DeanDashboard, and
// DepartmentMonitorDashboard — extracted from MonitorDashboard.js verbatim,
// plus a new "Activity Timeline" section (minute-level Logbook/Checklist/
// Assessment history) shared by both student and teacher views.

const TYPE_META = {
  logbook:    { icon: <FaBookMedical />,    label: 'Logbook',    color: '#4A90E2' },
  checklist:  { icon: <FaClipboardList />,  label: 'Checklist',  color: '#9B59B6' },
  assessment: { icon: <FaFileAlt />,        label: 'Assessment', color: '#E67E22' }
};

const fmtDateTime = d => d ? new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

function ActivityTimeline({ url, token, emptyLabel, showFilledBy }) {
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setActivity(d.success ? d.activity : []); setLoading(false); })
      .catch(() => { setActivity([]); setLoading(false); });
  }, [url, token]);

  return (
    <Card>
      <SectionHeader icon={<FaHistory />} title="Activity Timeline (Minute-Level)" color={MAROON} />
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:20 }}><Spinner size={28} /></div>
      ) : !activity?.length ? (
        <p style={{ color:'#999', textAlign:'center', padding:16, fontSize:13 }}>{emptyLabel}</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:340, overflowY:'auto' }}>
          {activity.map((a, i) => {
            const meta = TYPE_META[a.type] || TYPE_META.logbook;
            return (
              <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'8px 10px', background:'#f8f9fa', borderRadius:8, borderLeft:`3px solid ${meta.color}` }}>
                <span style={{ color:meta.color, fontSize:14, marginTop:2, flexShrink:0 }}>{meta.icon}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:700, fontSize:12.5, color:'#1a1a2e' }}>{a.title}</span>
                    <Badge label={meta.label} color={meta.color} bg={meta.color+'18'} />
                    <Badge label={a.status} color="#fff" bg={rateColor(a.status === 'approved' || a.status === 'reviewed' ? 100 : a.status === 'rejected' ? 0 : 50)} />
                  </div>
                  <div style={{ fontSize:11, color:'#777', marginTop:3 }}>
                    {a.courseName && <span style={{ marginRight:8 }}>{a.courseName}</span>}
                    {a.studentName && <span style={{ marginRight:8 }}>Student: <strong>{a.studentName}</strong></span>}
                    {showFilledBy && a.filledBy && <span style={{ marginRight:8 }}>Filled by: <strong>{a.filledBy}</strong></span>}
                  </div>
                  <div style={{ fontSize:11, color:'#999', marginTop:3, display:'flex', gap:12, flexWrap:'wrap' }}>
                    {a.filledAt && <span>Filled: {fmtDateTime(a.filledAt)}</span>}
                    {a.verifiedAt && <span style={{ color:'#27AE60', fontWeight:600 }}>Verified by {a.verifiedBy || 'reviewer'} · {fmtDateTime(a.verifiedAt)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Student Detail Modal ─────────────────────────────────────────────────────
export function StudentDetailModal({ studentId, onClose, token, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    setLoading(true);
    fetch(`${api}/monitor/student/${studentId}`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [studentId, api, token]);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      await generateStudentReportPDF({ data, collegeName: collegeConfig.collegeName, logoSrc: collegeConfig.logo });
    } catch (_) {
      alert('Failed to generate report PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Student Analytics" wide>
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
      ) : !data ? (
        <p style={{ color:'#999', textAlign:'center', padding:30 }}>Failed to load student data.</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Profile header */}
          <div style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:MAROON+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, color:MAROON, fontWeight:700, flexShrink:0 }}>
              {(data.student.name||'?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize:18, fontWeight:800, color:'#1a1a2e' }}>{data.student.name}</div>
              <div style={{ fontSize:12, color:'#666', marginTop:2 }}>
                {data.student.registrationNumber && <span style={{ marginRight:10 }}>Reg: <strong>{data.student.registrationNumber}</strong></span>}
                {data.student.email}
              </div>
              <div style={{ fontSize:12, color:'#888', marginTop:3 }}>
                {data.student.courses.map(c => c.courseName).filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8 }}>
              <Badge label={data.student.isActive ? 'Active' : 'Inactive'} color='#fff' bg={data.student.isActive ? '#27AE60' : '#E74C3C'} />
              <button
                onClick={handleDownloadReport}
                disabled={downloading}
                style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', border:`1px solid ${MAROON}30`, background:MAROON+'10', color:MAROON, borderRadius:8, fontWeight:700, fontSize:12, cursor: downloading ? 'default' : 'pointer' }}
              >
                <FaDownload size={11} /> {downloading ? 'Generating…' : 'Download PDF'}
              </button>
            </div>
          </div>

          {/* Logbook stat cards */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10 }}>
            {[
              { icon:<FaBookMedical />, label:'Total Entries', value:data.logbook.total, color:'#4A90E2' },
              { icon:<FaCheckCircle />, label:'Approved', value:data.logbook.approved, color:'#27AE60' },
              { icon:<FaClock />, label:'Pending', value:data.logbook.pending, color:'#F39C12' },
              { icon:<FaThumbsUp />, label:'Approval Rate', value:`${data.logbook.approvalRate}%`, color:rateColor(data.logbook.approvalRate) },
            ].map((c,i) => <StatCard key={i} {...c} />)}
          </div>

          {/* Monthly trend sparkline */}
          {data.logbook.monthlyTrend?.length > 0 && (
            <Card>
              <SectionHeader icon={<FaChartBar />} title="Monthly Submission Trend (12 Months)" color={MAROON} />
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.logbook.monthlyTrend} margin={{ top:0, right:10, left:-20, bottom:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize:10 }} />
                  <YAxis tick={{ fontSize:10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Submissions" fill="#4A90E2" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Module breakdown */}
          {data.logbook.moduleBreakdown?.length > 0 && (
            <Card>
              <SectionHeader icon={<FaLayerGroup />} title="Module-wise Performance" color="#9B59B6" />
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>
                      {['Module','Total','Approved','Pending','Rejected','Rate'].map(h => (
                        <th key={h} style={{ background:'#f8f9fa', padding:'8px 10px', textAlign: h==='Module'?'left':'center', fontWeight:700, color:'#555', borderBottom:'2px solid #e9ecef', fontSize:11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.logbook.moduleBreakdown.map((m,i) => {
                      const rate = m.total > 0 ? Math.round(m.approved / m.total * 100) : 0;
                      return (
                        <tr key={i} style={{ borderBottom:'1px solid #f0f0f0' }}>
                          <td style={{ padding:'8px 10px', fontWeight:600 }}>{m.name}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center' }}>{m.total}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center', color:'#27AE60', fontWeight:700 }}>{m.approved}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center', color:'#F39C12', fontWeight: m.pending>0?700:400 }}>{m.pending}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center', color:'#E74C3C', fontWeight: m.rejected>0?700:400 }}>{m.rejected}</td>
                          <td style={{ padding:'8px 10px', textAlign:'center' }}>
                            <Badge label={`${rate}%`} color='#fff' bg={rateColor(rate)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* DRP status */}
          {data.drp && (
            <Card style={{ borderLeft:`4px solid ${MAROON}` }}>
              <SectionHeader icon={<FaHospital />} title="DRP (Rural Posting) Status" color={MAROON} />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10, marginBottom:12 }}>
                {[
                  { label:'Hospital', value:data.drp.hospitalName || '—', color:'#4A90E2' },
                  { label:'From', value:fmtShort(data.drp.fromDate), color:'#27AE60' },
                  { label:'To', value:fmtShort(data.drp.toDate), color:'#27AE60' },
                  { label:'DRP Incharge', value:data.drp.inchargeName || '—', color:'#9B59B6' },
                ].map((item,i) => (
                  <div key={i} style={{ background:'#f8f9fa', borderRadius:8, padding:'8px 12px' }}>
                    <div style={{ fontSize:10, color:'#888', fontWeight:600 }}>{item.label.toUpperCase()}</div>
                    <div style={{ fontSize:13, fontWeight:700, color:item.color, marginTop:3 }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:12 }}>
                {[
                  { label:'Attendance', value:data.drp.attendanceCount, color:'#16a085' },
                  { label:'Diary', value:data.drp.diaryCount, color:'#2563eb' },
                  { label:'Operations', value:data.drp.operationsCount, color:'#7c3aed' },
                  { label:'Public Health', value:data.drp.publicHealthCount, color:'#dc6803' },
                ].map((item,i) => (
                  <div key={i} style={{ background:item.color+'10', border:`1px solid ${item.color}25`, borderRadius:8, padding:'8px', textAlign:'center' }}>
                    <div style={{ fontSize:20, fontWeight:800, color:item.color }}>{item.value}</div>
                    <div style={{ fontSize:10, color:'#555', marginTop:2 }}>{item.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                <div>
                  <div style={{ fontSize:11, color:'#888', marginBottom:6 }}>Incharge Verification</div>
                  <VerifDots v={data.drp.verification} />
                </div>
                <Badge
                  label={data.drp.certified ? '✓ Certificate Issued' : 'Not Certified'}
                  color='#fff'
                  bg={data.drp.certified ? MAROON : '#999'}
                />
              </div>
              {data.drp.historyCount > 0 && (
                <p style={{ margin:'10px 0 0', fontSize:12, color:'#888' }}>Past DRP postings: <strong>{data.drp.historyCount}</strong></p>
              )}
            </Card>
          )}

          <ActivityTimeline
            url={`${api}/monitor/student/${studentId}/activity`}
            token={token}
            emptyLabel="No logbook, checklist, or assessment activity recorded yet."
            showFilledBy
          />
        </div>
      )}
    </Modal>
  );
}

// ─── Teacher Detail Modal ─────────────────────────────────────────────────────
export function TeacherDetailModal({ teacherId, onClose, token, api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teacherId) return;
    setLoading(true);
    fetch(`${api}/monitor/teacher/${teacherId}`, { headers:{ Authorization:`Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.success) setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [teacherId, api, token]);

  return (
    <Modal onClose={onClose} title="Faculty Analytics">
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
      ) : !data ? (
        <p style={{ color:'#999', textAlign:'center', padding:30 }}>Failed to load data.</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ display:'flex', gap:14, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ width:56, height:56, borderRadius:'50%', background:MAROON+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, color:MAROON, fontWeight:700, flexShrink:0 }}>
              {(data.teacher.name||'?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ fontSize:18, fontWeight:800, color:'#1a1a2e' }}>
                  {data.teacher.name}
                  {data.teacher.leavingDate && (
                    <span style={{ fontSize:12, fontWeight:600, color:'#b45309', marginLeft:6 }}>
                      (left {new Date(data.teacher.leavingDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })})
                    </span>
                  )}
                </div>
                {data.teacher.isHOD && <Badge label="HOD" color='#fff' bg={MAROON} />}
              </div>
              <div style={{ fontSize:12, color:'#666', marginTop:2 }}>{data.teacher.email}</div>
              <div style={{ fontSize:12, color:'#888', marginTop:3 }}>
                {data.teacher.courses.map(c => c.courseName).filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10 }}>
            {[
              { icon:<FaUsers />, label:'Students Assigned', value:data.stats.totalStudents, color:'#4A90E2' },
              { icon:<FaBookMedical />, label:'Total Entries', value:data.stats.totalEntries, color:'#9B59B6' },
              { icon:<FaCheckCircle />, label:'Reviewed', value:data.stats.reviewed, color:'#27AE60' },
              { icon:<FaClock />, label:'Pending', value:data.stats.pending, color:'#F39C12' },
            ].map((c,i) => <StatCard key={i} {...c} />)}
          </div>

          <Card>
            <SectionHeader icon={<FaChartBar />} title="Monthly Review Activity (12 Months)" color={MAROON} />
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data.stats.monthlyActivity} margin={{ top:0, right:10, left:-20, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize:10 }} />
                <YAxis tick={{ fontSize:10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Reviews" fill="#9B59B6" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <ActivityTimeline
            url={`${api}/monitor/teacher/${teacherId}/activity`}
            token={token}
            emptyLabel="No reviewed logbook, checklist, or assessment activity yet."
          />
        </div>
      )}
    </Modal>
  );
}
